import React, {useEffect, useMemo, useRef, useState} from 'react';
import ArrTreeView from '../../common/arr-tree-view/ArrTreeView';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../shared/interfaces';
import {ISavedSearchTreeViewResponse} from '../../../api/types/savedSearch.type';
import {
  useRenameNode,
  useSavedSearchTreeView,
} from '../../../hooks/saved-search/savedSearch';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../shared/constants';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {ArrDrawer} from '../../common/arr-drawer/ArrDrawer';
import {Box, Typography} from '@mui/material';
import ArrButton from '../../common/arr-button/ArrButton';
import {useGetMySearchTreeDetails} from '../../../hooks/search/useSearch';
import {
  useDeleteSavedSearch,
  useGetShareGroups,
  useMoveSavedSearches,
  useShareSavedSearch,
  useBatchUploadSavedSearch,
  useDownloadTemplate,
} from '../../../hooks/saved-search/useSavedSearch';
import {SearchesTreeData} from '../../../api/types/searchType.type';
import ArrRadio from '../../common/arr-radio/ArrRadio';
import {t} from 'i18next';
import ArrModal from '../../common/arr-modal/ArrModal';
import {ReactComponent as DownloadIcon} from '../../../assets/images/download_icon.svg';
import {ReactComponent as InfoIcon} from '../../../assets/images/info-icon.svg';
import './SavedSearchTree.scss';
import {
  expectedHeaders,
  validateHeaders,
  savedSearchMoreMenu,
  getExpandedIdsForNode,
} from '../SavedSearchUtils';
import ExcelJS from 'exceljs';
import {getUUID} from '../../../api/endpoints/authenticationApi';
import {downloadTemplate} from '../../../api/endpoints/savedSearchApi';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {setSelectedTreeItemId} from '../../../redux/slice/savedSearchSlice';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';
import {
  getAllNestedIds,
  getSavedSearchRootCategoryName,
} from '../saved-search-utils';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
} from '../../../constants/analyticsConstants';
import {
  trackSavedSearchGroupMenuAction,
  trackBatchUploadDownloadTemplateClick,
} from '../../../utils/analyticsUtils';

interface ISavedSearchTree {
  getSelectedTreeItem: (items: string[]) => void;
  getSelectedTreeItemData?: (
    item?: TreeViewBaseItem<ExtendedTreeItemProps>,
    allItems?: TreeViewBaseItem<ExtendedTreeItemProps>[]
  ) => void;
}

const BATCH_UPLOAD_TEMPLATE_FILE_NAME = 'BatchSearchTemplate.xlsx';
const BATCH_UPLOAD_TEMPLATE_FILE_EXTENSION = 'xlsx';
const NEW_GROUP_INPUT_SELECTOR = 'input.edit-input';
const NEW_GROUP_INPUT_WATCH_MS = 30000;
const NEW_GROUP_INPUT_HOLD_MS = 1500;

const SavedSearchTree: React.FC<ISavedSearchTree> = ({
  getSelectedTreeItem,
  getSelectedTreeItemData,
}) => {
  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();

  const storedSelectedItem = useSelector(
    (state: RootState) => state.savedSearch.selectedTreeItemId
  );
  const {data: treeData, refetch} = useSavedSearchTreeView();
  const {mutate: updateRenameNode} = useRenameNode();
  const [selectedItem, setSelectedItem] = useState<string>('');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType>(ToastType.Success);
  const [openMoveDrawer, setOpenMoveDrawer] = useState(false);
  const [selectedDrawerItem, setSelectedDrawerItem] = useState<string>('');
  const [openShareModal, setOpenShareModal] = useState(false);
  const [shareWith, setShareWith] = useState('');
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [treeKey, setTreeKey] = useState<number>(0);
  const treeContainerRef = useRef<HTMLDivElement | null>(null);
  const newGroupInputObserverRef = useRef<MutationObserver | null>(null);
  const newGroupInputWatchTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const holdNewGroupInputRef = useRef(false);
  const releaseNewGroupHoldTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const pinnedExpandedIdsRef = useRef<string[]>([]);
  const expandAttemptIdsRef = useRef<Set<string>>(new Set());
  const {data: mySearchTreeDetailData} =
    useGetMySearchTreeDetails(openMoveDrawer);
  const {mutate: moveSavedSearches} = useMoveSavedSearches();
  const {mutate: deleteSearch} = useDeleteSavedSearch();
  const {data: sharedSecurityGroups} = useGetShareGroups(openShareModal);
  const {mutate: shareSearch} = useShareSavedSearch();
  const DefaultSecGroupId = '1';
  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );
  const [selectedTreeItem, setSelectedTreeItem] = useState<
    ISavedSearchTreeViewResponse | undefined
  >(undefined);
  const [showBatchUploadModal, setShowBatchUploadModal] =
    useState<boolean>(false);
  const [fileName, setFileName] = useState<string>('');
  const [xlFile, setXLFile] = useState<File | string>('');
  const [isError, setIsError] = useState(false);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const {data: downloadTemplateData, mutate: downloadTemplateMutate} =
    useDownloadTemplate();
  const {
    mutate: batchUploadSavedSearchMutate,
    data: batchUploadResponse,
    error: batchUploadError,
  } = useBatchUploadSavedSearch();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const hasRestoredTreeState = useRef(false);
  const hasAppliedInitialSelection = useRef(false);

  const transformToTreeItems = (
    flatList: ISavedSearchTreeViewResponse[] | undefined
  ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
    if (!Array.isArray(flatList)) return [];

    const nodeMap = new Map<string, TreeViewBaseItem<ExtendedTreeItemProps>>();
    const tree: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];

    // First pass: create all nodes and store in map
    for (const node of flatList) {
      nodeMap.set(node.id, {
        label: node.textWithoutCount || node.text,
        showMoreOptions: true,
        children: [],
        isDefault: false,
        tooltipTitle: node.toolTipRef_Title,
        tooltipContent: node.toolTipRef_Content,
        ...node,
        isGroup: false,
        id: node.id,
      });
    }

    // Second pass: build tree and set flags
    for (const node of flatList) {
      const current = nodeMap.get(node.id);
      if (!current) continue;

      if (node.parent && node.parent !== '#' && nodeMap.has(node.parent)) {
        const parent = nodeMap.get(node.parent)!;
        (parent.children ??= []).push(current);
        parent.isGroup = true;
      } else if (!node.parent || node.parent === '#') {
        // Treat "#" or undefined/null as root
        tree.push(current);
      }
      // Else: skip orphaned children whose parent is missing
    }

    return tree;
  };

  const transformMySearchTreeDetailsNode = (
    flatList: SearchesTreeData[] | undefined
  ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
    if (!Array.isArray(flatList)) return [];

    const nodeMap = new Map<string, TreeViewBaseItem<ExtendedTreeItemProps>>();
    const tree: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];

    // First pass: create all nodes and store in map
    flatList
      .filter((node) => node.text !== 'My Saved Search')
      .forEach((node) => {
        nodeMap.set(node.id, {
          label: node.text,
          showMoreOptions: true,
          children: [],
          isDefault: false,
          isGroup: 'false',
          tooltipTitle: node.toolTipRef_Title || '',
          tooltipContent: node.toolTipRef_Content || '',
          ...node,
        });
      });

    // Second pass: build tree and set flags
    flatList.forEach((node) => {
      const current = nodeMap.get(node.id);
      if (current)
        if (node.parent && nodeMap.has(node.parent)) {
          const parent = nodeMap.get(node.parent)!;
          (parent.children ??= []).push(current);
          parent.isGroup = 'true';
        } else {
          tree.push(current);
          current.isDefault = true;
        }
    });

    return tree;
  };

  const transformedTree = transformToTreeItems(treeData);

  const transformedSearchTreeDetails = transformMySearchTreeDetailsNode(
    mySearchTreeDetailData?.searchesTreeData
  );

  useEffect(() => {
    if (!treeData || !Array.isArray(treeData)) return;
    if (hasAppliedInitialSelection.current) return;

    hasRestoredTreeState.current = false;

    // If Redux has stored value
    if (storedSelectedItem) {
      const storedNode = treeData.find(
        (node) => node.id === storedSelectedItem
      );

      if (storedNode) {
        setSelectedItem(storedNode.id);
        setSelectedItems([storedNode.id]);
        setSelectedTreeItem(storedNode);
        getSelectedTreeItem([storedNode.id]);
        getSelectedTreeItemData?.(
          storedNode as unknown as TreeViewBaseItem<ExtendedTreeItemProps>
        );
        hasAppliedInitialSelection.current = true;
        return;
      }
    }

    // fallback to first root node
    const firstNode = treeData.find(
      (node) => node.parent === '#' || node.parent === '-1'
    );

    if (firstNode) {
      setSelectedItem(firstNode.id);
      setSelectedItems([firstNode.id]);
      setSelectedTreeItem(firstNode);
      getSelectedTreeItem([firstNode.id]);
      getSelectedTreeItemData?.(
        firstNode as unknown as TreeViewBaseItem<ExtendedTreeItemProps>
      );
      hasAppliedInitialSelection.current = true;
    }
  }, [treeData]);

  useEffect(() => {
    if (downloadTemplateData) {
      handleDownload();
    }
  }, [downloadTemplateData]);

  useEffect(() => {
    if (batchUploadError) {
      if ((batchUploadError as {status?: number}).status == 500) {
        triggerToast(
          t('savedSearch.batchUpload.errorUniqueName'),
          ToastType.Error
        );
      } else {
        triggerToast(batchUploadError.message, ToastType.Error);
      }
    } else if (batchUploadResponse) {
      triggerToast(
        t('savedSearch.batchUpload.successMessage'),
        ToastType.Success
      );
      setShowBatchUploadModal(false);
      setFileName('');
      setTimeout(() => {
        refetch();
      }, 3000);
    }
  }, [batchUploadResponse, batchUploadError]);

  useEffect(() => {
    if (
      !hasRestoredTreeState.current &&
      storedSelectedItem &&
      transformedTree.length > 0
    ) {
      const selectedNodeIsInTree = transformedTree.some(function containsNode(
        node: TreeViewBaseItem<ExtendedTreeItemProps>
      ): boolean {
        return (
          node.id === storedSelectedItem ||
          (node.children ?? []).some(containsNode)
        );
      });
      // A new group is selected before the reloaded tree contains it.
      // Remounting now would collapse the parent and drop the name field.
      if (!selectedNodeIsInTree) return;

      const expanded = getExpandedIdsForNode(
        transformedTree,
        storedSelectedItem
      );
      setExpandedIds(expanded);
      setTreeKey((prev) => prev + 1);
      hasRestoredTreeState.current = true;
    }
  }, [storedSelectedItem, JSON.stringify(transformedTree)]);

  const handleSelected = (item: string[] | null) => {
    const ids = Array.isArray(item) ? item.filter(Boolean) : [];

    if (ids.length > 0 && treeData) {
      const primaryId = ids[ids.length - 1];
      setSelectedItems(ids);
      setSelectedItem(primaryId);
      dispatch(setSelectedTreeItemId(primaryId));
      getSelectedTreeItem(ids);
      const foundNodes = ids
        .map((id) => treeData.find((data: any) => data.id == id))
        .filter(Boolean) as ISavedSearchTreeViewResponse[];
      const foundItem = foundNodes[foundNodes.length - 1];
      setSelectedTreeItem(foundItem);
      const treeNodes = foundNodes.map(
        (node) =>
          ({
            ...node,
            label: node.textWithoutCount || node.text,
            securitygroupsearchid: node.saveSearchSecurityGroupId,
          }) as TreeViewBaseItem<ExtendedTreeItemProps>
      );
      getSelectedTreeItemData?.(
        treeNodes.at(-1),
        treeNodes.length ? treeNodes : undefined
      );

      const categoryName = getSavedSearchRootCategoryName(treeData, primaryId);
      if (categoryName) {
        trackGAEvent({
          eventName: GA_EVENT_NAMES.CLICK,
          click: {
            name: categoryName,
            type: GA_CLICK_TYPES.LINK,
            region: GA_REGIONS.SAVED_SEARCH,
            action: t('gaTracking.global.selectedSavedSearchCategory'),
          },
        });
      }
    } else {
      setSelectedItems([]);
      setSelectedItem('');
      setSelectedTreeItem(undefined);
      dispatch(setSelectedTreeItemId(null));
      getSelectedTreeItem([]);
    }
  };

  const onRenameNode = async (
    text: string,
    node: TreeViewBaseItem<ExtendedTreeItemProps>
  ) => {
    // The scroll that reveals a new group can blur the field. Ignore that
    // save so the default name is not committed before the user can edit it.
    if (holdNewGroupInputRef.current) return;

    const payload = {
      searchGroupLabel: text !== '' ? text : node.label,
      searchGroupID: Number(node.id),
      parentSearchGroupID: node.parent ? Number(node.parent) : 0,
      savedSearchId: 0,
      searchGroupType: '',
      searchGroupSecurityGroupId: node.securitygroupsearchid ?? 0,
      toolTipRef: '',
    };
    updateRenameNode(payload, {
      onSuccess: (data) => {
        setToastMessage(data.message);
        setToastStatus(ToastType.Success);
        setShowToast(true);
        refetch();
      },
      onError: (error) => {
        setToastMessage(error.message);
        setToastStatus(ToastType.Error);
        setShowToast(true);
      },
    });
  };

  const stopWatchingForNewGroupInput = () => {
    newGroupInputObserverRef.current?.disconnect();
    newGroupInputObserverRef.current = null;
    if (newGroupInputWatchTimeoutRef.current) {
      clearTimeout(newGroupInputWatchTimeoutRef.current);
      newGroupInputWatchTimeoutRef.current = null;
    }
  };

  const releaseNewGroupHold = () => {
    holdNewGroupInputRef.current = false;
    pinnedExpandedIdsRef.current = [];
    expandAttemptIdsRef.current.clear();
    if (releaseNewGroupHoldTimeoutRef.current) {
      clearTimeout(releaseNewGroupHoldTimeoutRef.current);
      releaseNewGroupHoldTimeoutRef.current = null;
    }
    stopWatchingForNewGroupInput();
  };

  const scrollInputToCenter = (input: HTMLInputElement) => {
    let parent = input.parentElement;
    while (parent) {
      const {overflowY} = window.getComputedStyle(parent);
      const canScroll =
        (overflowY === 'auto' || overflowY === 'scroll') &&
        parent.scrollHeight > parent.clientHeight;
      if (canScroll) {
        const parentRect = parent.getBoundingClientRect();
        const inputRect = input.getBoundingClientRect();
        const nextTop =
          parent.scrollTop +
          (inputRect.top - parentRect.top) -
          parentRect.height / 2 +
          inputRect.height / 2;
        parent.scrollTop = Math.max(0, nextTop);
        return;
      }
      parent = parent.parentElement;
    }
  };

  const focusNewGroupInput = (input: HTMLInputElement) => {
    scrollInputToCenter(input);
    input.focus({preventScroll: true});
    input.setSelectionRange(0, input.value.length);
  };

  const pinParentExpanded = () => {
    const parentIds = [
      ...getExpandedIdsForNode(transformedTree, selectedItem),
      selectedItem,
    ].filter(Boolean);
    const ids = Array.from(new Set(parentIds));
    if (!ids.length) return;
    pinnedExpandedIdsRef.current = ids;
    setExpandedIds((prev) => Array.from(new Set([...prev, ...ids])));
  };

  const keepPinnedParentsExpanded = () => {
    const container = treeContainerRef.current;
    if (!container || !holdNewGroupInputRef.current) return;

    pinnedExpandedIdsRef.current.forEach((id) => {
      const treeItem = container.querySelector(
        `[data-testid="${id}-tree-view-tree-item"]`
      );
      if (!treeItem) return;
      if (treeItem.getAttribute('aria-expanded') !== 'false') {
        expandAttemptIdsRef.current.delete(id);
        return;
      }
      if (expandAttemptIdsRef.current.has(id)) return;

      const toggle = container.querySelector(
        `[data-testid="tree-view-expand-collapse-${id}"]`
      );
      if (!(toggle instanceof HTMLElement)) return;
      expandAttemptIdsRef.current.add(id);
      toggle.click();
    });
  };

  const watchForNewGroupInput = () => {
    const container = treeContainerRef.current;
    if (!container) return;

    stopWatchingForNewGroupInput();
    holdNewGroupInputRef.current = true;
    pinParentExpanded();
    const existingInput = container.querySelector(NEW_GROUP_INPUT_SELECTOR);

    const armNewGroupRelease = (input: HTMLInputElement) => {
      if (releaseNewGroupHoldTimeoutRef.current) return;
      input.addEventListener('input', releaseNewGroupHold, {once: true});
      input.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Enter') releaseNewGroupHold();
        },
        {once: true}
      );
      releaseNewGroupHoldTimeoutRef.current = setTimeout(
        releaseNewGroupHold,
        NEW_GROUP_INPUT_HOLD_MS
      );
    };

    const revealInput = () => {
      keepPinnedParentsExpanded();
      const input = container.querySelector<HTMLInputElement>(
        NEW_GROUP_INPUT_SELECTOR
      );
      if (!input || input === existingInput || !input.isConnected) return;

      if (document.activeElement !== input) {
        focusNewGroupInput(input);
      }
      if (document.activeElement === input) armNewGroupRelease(input);
    };

    const observer = new MutationObserver(() => {
      keepPinnedParentsExpanded();
      revealInput();
    });
    newGroupInputObserverRef.current = observer;
    observer.observe(container, {
      attributes: true,
      attributeFilter: ['aria-expanded'],
      childList: true,
      subtree: true,
    });
    newGroupInputWatchTimeoutRef.current = setTimeout(() => {
      stopWatchingForNewGroupInput();
      releaseNewGroupHold();
    }, NEW_GROUP_INPUT_WATCH_MS);
    revealInput();
  };

  useEffect(() => {
    const container = treeContainerRef.current;
    if (!container) return;

    const handleFocusOut = (event: FocusEvent) => {
      if (!holdNewGroupInputRef.current) return;
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (!target.classList.contains('edit-input')) return;

      event.stopPropagation();
      const input = target;
      setTimeout(() => {
        if (!holdNewGroupInputRef.current || !input.isConnected) return;
        input.focus({preventScroll: true});
      }, 0);
    };

    container.addEventListener('focusout', handleFocusOut, true);
    return () => {
      container.removeEventListener('focusout', handleFocusOut, true);
      stopWatchingForNewGroupInput();
      releaseNewGroupHold();
    };
  }, []);

  const handleMenuItemClick = (item: {id: string}) => {
    trackSavedSearchGroupMenuAction(trackGAEvent, {
      actionId: item.id,
      categoryName: getSavedSearchRootCategoryName(treeData ?? [], selectedItem),
    });

    if (item.id === 'add_group') {
      watchForNewGroupInput();
    } else {
      stopWatchingForNewGroupInput();
    }

    if (item.id === 'move') setOpenMoveDrawer(true);
    if (item.id === 'share') setOpenShareModal(true);
    if (item.id === 'delete') setOpenDeleteModal(true);
    if (item.id === 'batch_upload') setShowBatchUploadModal(true);
    if (item.id === 'add_group_level_error') {
      triggerToast(t('savedSearch.maxLevelError'), ToastType.Error);
    }
  };

  const handleDrawerSelection = (item: string[] | null) => {
    item ? setSelectedDrawerItem(item[0]) : null;
  };

  const onClickDrawerSaveButton = () => {
    setShowLoader(true);
    const fromItem = treeData?.find((data) => data.id === selectedItem);
    const toItem = treeData?.find(
      (data) => data.id === String(selectedDrawerItem)
    );
    const AlwaysAllowMoveIds = getAllNestedIds(treeData);

    const hasFromIndex = (fromItem?.indexData?.length ?? 0) > 0;
    const hasToIndex = (toItem?.indexData?.length ?? 0) > 0;

    const hasFromStandardIndex =
      fromItem?.indexData?.length === 1 && fromItem?.indexData[0].indexId === 0;

    const hasFromCustomIndexWithNoSecurityGroup =
      fromItem?.indexData?.some(
        (item: any) =>
          item.indexId !== 0 && item.indexSecurityGroupIds.length === 0
      ) ?? false;
    const toDefaultSecGroup =
      toItem?.id === DefaultSecGroupId &&
      hasFromIndex &&
      fromItem?.indexData?.some((item: any) =>
        item.indexSecurityGroupIds.includes(Number(DefaultSecGroupId))
      ) &&
      !hasFromCustomIndexWithNoSecurityGroup;
    const canMove =
      AlwaysAllowMoveIds.includes(toItem?.id) ||
      toDefaultSecGroup ||
      hasFromStandardIndex ||
      (!hasFromCustomIndexWithNoSecurityGroup &&
        hasFromIndex &&
        hasToIndex &&
        [
          ...new Set(
            fromItem?.indexData?.flatMap(
              (item) => item.indexSecurityGroupIds
            ) ?? []
          ),
        ].every((id) =>
          new Set(
            toItem?.indexData?.flatMap((item) => item.indexSecurityGroupIds) ??
              []
          ).has(id)
        ));

    if (canMove) {
      const movePayload = {
        searchGroupID: parseInt(selectedItem),
        parentSearchGroupID: parseInt(selectedDrawerItem),
        searchGroupLabel: fromItem?.text ?? '',
        savedSearchId: 0,
        searchGroupType: 'SGSHARED',
        toolTipRef: fromItem?.toolTipRef_Title ?? 'toolTipRefTitle',
        searchGroupSecurityGroupId: 0,
      };

      moveSavedSearches(movePayload, {
        onSuccess: (result) => {
          setShowLoader(false);
          triggerToast(result.message, ToastType.Success);
          setOpenMoveDrawer(false);
          refetch();
          setSelectedDrawerItem('');
        },
        onError: () => {
          setShowLoader(false);
          triggerToast(
            t('savedSearch.savedSearchTreeFlyout.saveError'),
            ToastType.Error
          );
        },
      });
    } else {
      setShowLoader(false);
      triggerToast(
        t('savedSearch.savedSearchTreeFlyout.saveError'),
        ToastType.Error
      );
    }
  };

  const handleShare = () => {
    const sharedGroup = sharedSecurityGroups?.securityGroups.find(
      (item) => item.toGroupID.toString() == shareWith
    );

    const item = treeData?.find((data) => data.id == selectedItem);

    if (!sharedGroup || !selectedItem) {
      return;
    }
    const sharePayload = {
      searchID: parseInt(selectedItem),
      toGroupID: sharedGroup.toGroupID,
      isSearchGroup: true,
      destSecurityGroupID: sharedGroup.securityGroupID,
      notificationURL: sharedGroup?.notificationURL ?? '',
      emailId: sharedGroup?.emailId ?? '',
      name: item?.text ?? 'string',
      shareType: 'SGSHARED',
      originalSearchID: sharedGroup.originalSearchID,
    };
    shareSearch(sharePayload, {
      onSuccess: (result) => {
        triggerToast(result.successMessage, ToastType.Success);
        setOpenShareModal(false);
        setTimeout(() => refetch(), 10);
        setShareWith('');
      },
      onError: () => {
        triggerToast(t('savedSearch.shareModal.shareError'), ToastType.Error);
      },
    });
  };

  const handleConfirmDelete = () => {
    const node = treeData?.find((item) => item.id == selectedItem);
    if (!node || !selectedItem) {
      return;
    }
    const deletePayload = {
      searchGroupID: Number(selectedItem),
      parentSearchGroupID: parseInt(selectedItem),
      searchGroupLabel: node.text,
      roleID: loginUserData.roleId,
      userID: loginUserData.userId,
      securityGroupID: node.saveSearchSecurityGroupId,
      projectId: loginUserData.projectId,
      savedSearchId: 0,
      searchGroupType: node?.parent,
      searchGroupSecurityGroupId: node.saveSearchSecurityGroupId ?? 0,
      toolTipRef: 'a',
    };
    deleteSearch(deletePayload, {
      onSuccess: (result) => {
        triggerToast(result.successMessage, ToastType.Success);
        setOpenDeleteModal(false);
        setSelectedItems([]);
        refetch();
      },
      onError: () => {
        triggerToast(t('savedSearch.deleteModal.deleteError'), ToastType.Error);
      },
    });
  };

  const triggerToast = (message: string, status: ToastType) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  };

  const handleFileValidation = (acceptedFiles: File[]) => {
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];
    const file = acceptedFiles[0];

    if (file) {
      if (allowedTypes.includes(file.type)) {
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const binaryStr = e.target?.result;
            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(binaryStr as ArrayBuffer);
            const worksheet = workbook.worksheets[0];
            if (!worksheet) {
              return;
            }
            const headersJson: (string | number)[][] = [];
            worksheet.eachRow((row) => {
              const values = row.values;
              if (!values) {
                return;
              }
              const rowValues = (
                Array.isArray(values)
                  ? values.slice(1)
                  : Object.values(values).slice(1)
              ).filter(
                (item): item is string | number =>
                  typeof item === 'string' || typeof item === 'number'
              );
              headersJson.push(rowValues);
            });
            const headersRow = headersJson[0] || [];
            const filteredheader = headersRow
              .filter((item) => item !== 0)
              .map(String);
            if (validateHeaders(filteredheader, expectedHeaders)) {
              setFileName(file.name);
              setXLFile(file);
              setIsError(false);
            } else {
              setIsError(true);
              triggerToast(
                t('savedSearch.batchUpload.errorValidFormat'),
                ToastType.Error
              );
            }
          } catch (error) {
            console.log(error);
          }
        };
        reader.readAsArrayBuffer(file);
      } else {
        triggerToast(
          t('savedSearch.batchUpload.errorValidExtension'),
          ToastType.Error
        );
      }
    }
  };
  const handleDownload = async () => {
    try {
      const blob = await downloadTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = BATCH_UPLOAD_TEMPLATE_FILE_NAME;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading the template:', error);
    }
  };

  const isGroup = useMemo(
    () => treeData?.find((item) => item.parent === selectedItem),
    [selectedItem]
  );

  const moreMenuOptions = useMemo(
    () =>
      savedSearchMoreMenu(
        loginUserData.roleId,
        selectedTreeItem?.parent ?? '',
        true,
        isGroup != undefined ? true : false
      ),
    [selectedItem]
  );

  return (
    <Box
      ref={treeContainerRef}
      className="saved-search-tree-container"
      data-testid="saved-saearch-tree-container"
    >
      <ArrTreeView
        key={treeKey}
        setSelectedItem={handleSelected}
        showTooltip
        itemsList={transformedTree}
        selectedItem={selectedItem}
        handleNoResultMessage={'Loading...'}
        allowGlobalSelect={false}
        treeSelectedItems={selectedItems}
        multipleSelection={false}
        isShowCheckbox={false}
        isShowCountLabel={true}
        showActions={true}
        onRenameNode={onRenameNode}
        MoreMenuOptions={moreMenuOptions}
        onMenuItemClick={handleMenuItemClick}
        menuDirection="bottom"
        menuClassName={`saved-tree-menu  ${
          selectedTreeItem?.parent !== '#'
            ? moreMenuOptions.find(
                (menuItem) => menuItem.id === 'batch_upload'
              ) !== undefined
              ? 'with-border-second-child'
              : 'with-border-first-child'
            : ''
        }`}
        expandSelect={false}
        defaultExpandedIds={expandedIds}
      />
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          onClose={() => {
            setToastMessage('');
            setShowToast(false);
          }}
          status={[toastStatus]}
        />
      )}
      {openMoveDrawer && (
        <ArrDrawer
          transition={300}
          onClose={() => setOpenMoveDrawer(false)}
          title={t('savedSearch.moveDrawer.title')}
          side="right"
          size="large"
          showButton={false}
          showHeader={false}
          className="search-group-move-drawer"
          isOpen={openMoveDrawer}
        >
          <Box className="search-group-move-drawer__header">
            <Box className="title-box">
              <Typography
                className=" typo-text headline3_size title"
                data-testid={`saved-search-tree-flyout-title`}
              >
                {t('savedSearch.savedSearchTreeFlyout.flyoutTitle')}
              </Typography>
            </Box>
            <Box className="action-buttons-box">
              <ArrButton
                variant="secondary"
                size="medium"
                onClick={() => {
                  setOpenMoveDrawer(false);
                }}
                disabled={false}
                aria-label={t('common.cancel')}
                dataTestId="saved-search-tree-cancel-button"
              >
                {t('common.cancel')}
              </ArrButton>
              <ArrButton
                disabled={!selectedDrawerItem}
                variant="primary"
                size="medium"
                type="submit"
                aria-label={t('common.save')}
                dataTestId="saved-search-tree-save-button"
                onClick={onClickDrawerSaveButton}
              >
                {t('common.save')}
              </ArrButton>
            </Box>
          </Box>
          <Box className="search-group-move-drawer__content">
            <ArrTreeView
              setSelectedItem={handleDrawerSelection}
              itemsList={transformedSearchTreeDetails}
              selectedItem={selectedDrawerItem}
              handleNoResultMessage={'Loading...'}
              allowGlobalSelect={false}
              treeSelectedItems={[selectedDrawerItem]}
              multipleSelection={false}
              isShowCountLabel={true}
              isShowCheckbox={false}
            />
          </Box>
        </ArrDrawer>
      )}
      <ArrModal
        showModal={openShareModal}
        onClose={() => {
          setShareWith('');
          setOpenShareModal(false);
        }}
        className="saved-search-share-modal"
        title={t('savedSearch.shareModal.title')}
        description={t('savedSearch.shareModal.description')}
        ariaLabel={t('savedSearch.shareModal.title')}
        ariaDescription={t('savedSearch.shareModal.description')}
        dataTestId="saved-search-share-modal"
        gaConfig={{
          name: t('gaTracking.global.shareSavedSearchSubpageName'),
          details: t('gaTracking.global.savedSearchGroupMenuDetails', {
            name:
              getSavedSearchRootCategoryName(treeData ?? [], selectedItem) ??
              '',
          }),
        }}
        buttons={[
          {
            id: '1',
            label: t('common.cancel'),
            onClick: () => {
              setShareWith('');
              setOpenShareModal(false);
            },
            variant: 'secondary',
            dataTestId: 'sst-cancel-btn',
          },
          {
            id: '2',
            label: t('common.save'),
            onClick: () => handleShare(),
            variant: 'primary',
            dataTestId: 'sst-save-btn',
            disabled: !shareWith,
            gaConfig: {
              name: t('gaTracking.global.saveClickName'),
              action: t('gaTracking.global.sharedSavedSearch'),
              details: t('gaTracking.global.sharedSavedSearchGroupDetails', {
                name:
                  sharedSecurityGroups?.securityGroups.find(
                    (item) => item.toGroupID.toString() == shareWith
                  )?.name ?? '',
              }),
            },
          },
        ]}
      >
        <Box className="modal-container">
          <ul data-testid={`saved-search-tree-ul`}>
            <li
              className="typo-text body2_size error-text"
              data-testid={`saved-search-tree-shared-with-li`}
            >
              <ArrRadio
                dataTestId="selectItem"
                value={shareWith}
                name={'selectItem'}
                data={
                  sharedSecurityGroups
                    ? sharedSecurityGroups.securityGroups.map((opt) => {
                        return {
                          label: opt.name,
                          value: opt.toGroupID.toString(),
                        };
                      })
                    : []
                }
                onChange={(e) => {
                  setShareWith(e);
                }}
              />
            </li>
          </ul>
        </Box>
      </ArrModal>
      <ArrModal
        showModal={openDeleteModal}
        title={t('savedSearch.deleteGroupModal.title')}
        description={t('savedSearch.deleteGroupModal.description')}
        ariaLabel={t('savedSearch.deleteGroupModal.title')}
        ariaDescription={t('savedSearch.deleteGroupModal.description')}
        onClose={() => setOpenDeleteModal(false)}
        dataTestId="savedsearcg-deletegroup-modal"
        buttons={[
          {
            id: 'cancel-delete',
            label: t('common.cancel'),
            onClick: () => setOpenDeleteModal(false),
            variant: 'secondary',
            dataTestId: 'ssd-cancel-del-btn',
          },
          {
            id: 'confirm-delete',
            label: t('common.ok'),
            onClick: () => {
              handleConfirmDelete();
            },
            variant: 'primary',
            dataTestId: 'ssd-confirm-del-btn',
          },
        ]}
        className="Delete-security-group-modal"
      />
      <ArrModal
        showModal={showBatchUploadModal}
        onClose={() => {
          setFileName('');
          setShowBatchUploadModal(false);
        }}
        className="saved-search-tree-modal"
        title={t('savedSearch.batchUpload.title')}
        ariaLabel={t('savedSearch.batchUpload.title')}
        dataTestId="savedsearch-batchupload-modal"
        gaConfig={{
          name: t('gaTracking.global.batchUploadSubpageName'),
          details: t('gaTracking.global.savedSearchGroupMenuDetails', {
            name:
              getSavedSearchRootCategoryName(treeData ?? [], selectedItem) ??
              '',
          }),
        }}
        titleEndIcon={
          <ArrTooltipWrapper
            title={t('savedSearch.batchUpload.title')}
            gaConfig={{
              details: t('savedSearch.batchUpload.title'),
              click: {},
            }}
            trigger={
              <InfoIcon
                fontSize="medium"
                className="saved-search-tree-info-icon label1_size"
                data-testid="savedSearch-batchUpload-info"
              />
            }
          >
            {t('savedSearch.batchUpload.titleInfo')}
          </ArrTooltipWrapper>
        }
        buttons={[
          {
            id: '1',
            label: t('common.cancel'),
            onClick: () => {
              setFileName('');
              setShowBatchUploadModal(false);
            },
            variant: 'secondary',
            dataTestId: 'ssb-cancel-btn',
          },
          {
            id: '2',
            label: t('common.save'),
            gaConfig: {
              name: t('gaTracking.global.saveClickName'),
              action: t('gaTracking.global.savedBatchUpload'),
            },
            onClick: () => {
              const formData = new FormData();
              formData.append('BatchSearchFile', xlFile);
              const params = {
                SearchGroupId: selectedItem,
                SecurityGroupId: selectedTreeItem?.securitygroupsearchid ?? 0,
                SessionId: getUUID(),
              };
              batchUploadSavedSearchMutate({formData, params});
            },
            variant: 'primary',
            dataTestId: 'ssb-save-btn',
            disabled: !fileName || isError,
          },
        ]}
      >
        <Box className="saved-search-tree-selection-container">
          <label className="label-text" data-testid={`saved-search-tree-label`}>
            <span
              className="typo-text body2_size"
              data-testid={`saved-search-file-name`}
            >
              {fileName || t('savedSearch.batchUpload.chooseFile')}
            </span>
          </label>

          <input
            ref={fileInputRef}
            style={{display: 'none'}}
            data-testid="file-input"
            type="file"
            onChange={(e) => {
              if (e.target.files) {
                e.target.blur();
                handleFileValidation(Array.from(e.target.files));
              }
            }}
            accept=".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          />
          <ArrButton
            variant="primary"
            size="medium"
            type="submit"
            aria-label={t('common.select')}
            dataTestId="saved-search-tree-select-button"
            onClick={() => {
              fileInputRef.current?.click();
            }}
          >
            {t('common.select')}
          </ArrButton>
        </Box>
        <ArrButton
          variant="text"
          size="medium"
          type="submit"
          aria-label={t('savedSearch.batchUpload.downloadTemplate')}
          dataTestId="saved-search-tree-download-button"
          startIcon={<DownloadIcon fontSize="medium" />}
          onClick={() => {
            trackBatchUploadDownloadTemplateClick(trackGAEvent, {
              categoryName: getSavedSearchRootCategoryName(
                treeData ?? [],
                selectedItem
              ),
              fileExtension: BATCH_UPLOAD_TEMPLATE_FILE_EXTENSION,
            });
            downloadTemplateMutate({});
          }}
        >
          {t('savedSearch.batchUpload.downloadTemplate')}
        </ArrButton>
      </ArrModal>
      {showLoader && (
        <ArrLoadingSpinner show={showLoader} overlay={true} isOpen={true} />
      )}
    </Box>
  );
};

export default SavedSearchTree;
